import React, { useCallback, useEffect, useRef, useState } from "react";
import { Platform, StyleSheet, View } from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import { ASSESSMENT_INTRO_DISPLAY_HEIGHT } from "../../../constants/assessmentConstants";

interface TurnBasedAssessmentAudioRendererProps {
  introText?: string;
  introAudio?: string;
  onIntroStart?: () => void;
  onIntroComplete?: () => void;
  useStreaming?: boolean;
  onAudioQueueReady?: (callback: (chunk: { seq: number; text: string; audio: string }) => void) => void;
  onChunkPlayStart?: (text: string) => void;
  onStreamDone?: (processRemaining: () => void) => void;
  onResetSequence?: (resetFn: () => void) => void;
  onAudioLevel?: (level: number) => void;
}

const TurnBasedAssessmentAudioRenderer = ({
  introText: initialIntroText,
  introAudio: initialIntroAudio,
  onIntroStart,
  onIntroComplete,
  useStreaming = false,
  onAudioQueueReady,
  onChunkPlayStart,
  onStreamDone,
  onResetSequence,
  onAudioLevel,
}: TurnBasedAssessmentAudioRendererProps) => {
  const [introText, setIntroText] = useState<string>(initialIntroText || "");
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioQueueRef = useRef<HTMLAudioElement[]>([]);

  const audioContextRef = useRef<AudioContext | null>(null);
  const meterGainRef = useRef<GainNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const analyserDataRef = useRef<Float32Array | null>(null);
  const meterRafIdRef = useRef<number | null>(null);
  const meterInitializedRef = useRef(false);
  const nextStartAtRef = useRef<number>(0);
  const nextSeqRef = useRef<number>(1);
  const pendingBySeqRef = useRef<Map<number, { seq: number; text: string; audio: string }>>(new Map());
  const hasPlayedRef = useRef(false);
  const hasStartedRef = useRef(false);
  const scrollViewRef = useRef<any>(null);

  const playAudio = useCallback(
    (audioData: string) => {
      if (Platform.OS === "web") {
        const audioSrc = `data:audio/mp3;base64,${audioData}`;
        const audio = new Audio(audioSrc);

        audio.onplay = () => {
          if (!hasStartedRef.current) {
            setIsPlaying(true);
            hasStartedRef.current = true;
            if (onIntroStart) {
              onIntroStart();
            }
          }
        };

        audio.onended = () => {
          if (audioQueueRef.current.length > 0) {
            const nextAudio = audioQueueRef.current.shift();
            if (nextAudio) {
              nextAudio.play().catch((error) => {
                console.error("Error playing next audio:", error);
              });
            }
          } else {
            setIsPlaying(false);
            if (onIntroComplete) {
              onIntroComplete();
            }
          }
        };

        audio.onerror = (error) => {
          console.error("Error playing audio:", error);
          if (audioQueueRef.current.length > 0) {
            const nextAudio = audioQueueRef.current.shift();
            if (nextAudio) {
              nextAudio.play().catch((error) => {
                console.error("Error playing next audio:", error);
              });
            }
          } else {
            setIsPlaying(false);
            if (onIntroComplete) {
              onIntroComplete();
            }
          }
        };

        if (!audioRef.current || audioRef.current.paused) {
          audioRef.current = audio;
          audio.play().catch((error) => {
            console.error("Error starting audio playback:", error);
            setIsPlaying(false);
            if (onIntroComplete) {
              onIntroComplete();
            }
          });
        } else {
          audioQueueRef.current.push(audio);
        }
      }
    },
    [onIntroStart, onIntroComplete]
  );

  useEffect(() => {
    if (initialIntroText !== undefined) {
      setIntroText(initialIntroText || "");
    }
  }, [initialIntroText]);

  useEffect(() => {
    if (introText && scrollViewRef.current) {
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      }, 0);
    }
  }, [introText]);

  const playedAudioChunksRef = useRef<Set<string>>(new Set());
  const lastProcessedAudioRef = useRef<string | null>(null);
  const pendingTextRef = useRef<string>("");

  const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = binaryString.charCodeAt(i);
    return bytes.buffer;
  };

  const handleChunkText = useCallback(
    (newText: string) => {
      pendingTextRef.current += newText;
      const lastChar = newText.slice(-1);
      if (/\s|[.!?]/.test(lastChar)) {
        setIntroText((prev) => prev + pendingTextRef.current);
        pendingTextRef.current = "";
      }
    },
    [setIntroText]
  );

  const scheduleWebAudioChunk = useCallback(
    async (chunk: { seq: number; text: string; audio: string }) => {
      if (Platform.OS !== "web") return;
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      const ctx = audioContextRef.current;

      if (!meterInitializedRef.current) {
        meterGainRef.current = ctx.createGain();
        analyserRef.current = ctx.createAnalyser();
        analyserRef.current.fftSize = 2048;
        analyserRef.current.smoothingTimeConstant = 0.8;
        analyserDataRef.current = new Float32Array(analyserRef.current.fftSize) as unknown as Float32Array;
        meterGainRef.current.connect(analyserRef.current);
        analyserRef.current.connect(ctx.destination);
        meterInitializedRef.current = true;

        const tick = () => {
          if (!analyserRef.current || !analyserDataRef.current) {
            meterRafIdRef.current = requestAnimationFrame(tick);
            return;
          }
          analyserRef.current.getFloatTimeDomainData(analyserDataRef.current as any);
          let sumSquares = 0;
          for (let i = 0; i < analyserDataRef.current.length; i++) {
            const v = analyserDataRef.current[i];
            sumSquares += v * v;
          }
          const rms = Math.sqrt(sumSquares / analyserDataRef.current.length);
          const level = Math.max(0, Math.min(1, Math.pow(rms / 0.35, 0.8)));
          if (onAudioLevel) onAudioLevel(level);
          meterRafIdRef.current = requestAnimationFrame(tick);
        };
        meterRafIdRef.current = requestAnimationFrame(tick);
      }
      if (ctx.state === "suspended") {
        await ctx.resume();
      }

      try {
        const arrayBuffer = base64ToArrayBuffer(chunk.audio);

        // TTS API sends MP3 audio - decode it
        const audioBuffer = await ctx.decodeAudioData(arrayBuffer);

        const startAt = Math.max(ctx.currentTime, nextStartAtRef.current || ctx.currentTime);
        const source = ctx.createBufferSource();
        source.buffer = audioBuffer;
        const gain = ctx.createGain();
        source.connect(gain);
        if (meterGainRef.current) {
          gain.connect(meterGainRef.current);
        } else {
          gain.connect(ctx.destination);
        }

        // small fade in/out to avoid clicks and mask micro gaps
        const ramp = 0.02;
        gain.gain.setValueAtTime(0.0, startAt);
        gain.gain.linearRampToValueAtTime(1.0, startAt + ramp);
        gain.gain.setValueAtTime(1.0, startAt + audioBuffer.duration - ramp);
        gain.gain.linearRampToValueAtTime(0.0, startAt + audioBuffer.duration);

        source.start(startAt);

        if (!hasStartedRef.current) {
          hasStartedRef.current = true;
          setIsPlaying(true);
          if (onIntroStart) onIntroStart();
        }
        handleChunkText(chunk.text);

        nextStartAtRef.current = startAt + audioBuffer.duration;
      } catch (e) {
        console.error("Error scheduling web audio chunk", e, { seq: chunk.seq });
      }
    },
    [onIntroStart, onChunkPlayStart, onAudioLevel]
  );

  const isProcessingRef = useRef(false);
  const processPendingChunksRef = useRef<(() => Promise<void>) | null>(null);
  const processRemainingChunksRef = useRef<(() => void) | null>(null);

  const processIncomingChunk = useCallback(
    async (incoming: { seq: number; text: string; audio: string }) => {
      if (Platform.OS === "web") {
        pendingBySeqRef.current.set(incoming.seq, incoming);
        const processPendingChunks = async () => {
          if (isProcessingRef.current) {
            return;
          }
          isProcessingRef.current = true;
          try {
            let processedAny = true;
            while (processedAny) {
              processedAny = false;
              if (pendingBySeqRef.current.has(nextSeqRef.current)) {
                const next = pendingBySeqRef.current.get(nextSeqRef.current)!;
                pendingBySeqRef.current.delete(nextSeqRef.current);
                try {
                  // eslint-disable-next-line no-await-in-loop
                  await scheduleWebAudioChunk(next);
                  nextSeqRef.current += 1;
                  processedAny = true;
                } catch (error) {
                  console.error("Error processing chunk", error, { seq: next.seq });
                  nextSeqRef.current += 1;
                  processedAny = true;
                }
              }
            }
          } finally {
            isProcessingRef.current = false;

            if (pendingBySeqRef.current.has(nextSeqRef.current)) {
              console.log("More chunks available after loop exit, processing again", {
                nextSeq: nextSeqRef.current,
                pendingSize: pendingBySeqRef.current.size,
              });
              setTimeout(() => {
                if (processPendingChunksRef.current) {
                  processPendingChunksRef.current().catch((error) => {
                    console.error("Error in recursive processing", error);
                  });
                }
              }, 0);
            }
          }
        };

        processPendingChunksRef.current = processPendingChunks;

        processRemainingChunksRef.current = () => {
          if (!isProcessingRef.current && processPendingChunksRef.current) {
            processPendingChunksRef.current().catch((error) => {
              console.error("Error processing remaining chunks", error);
            });
          }
        };

        await processPendingChunks();
        if (pendingBySeqRef.current.has(nextSeqRef.current) && !isProcessingRef.current) {
          setTimeout(() => {
            if (processPendingChunksRef.current && !isProcessingRef.current) {
              processPendingChunksRef.current().catch((error) => {
                console.error("Error in delayed processing", error);
              });
            }
          }, 0);
        }

        return;
      }
      const audioData = incoming.audio;
      const audioHash = audioData;
      if (!playedAudioChunksRef.current.has(audioHash)) {
        playedAudioChunksRef.current.add(audioHash);
        lastProcessedAudioRef.current = audioData;
        if (!hasPlayedRef.current) hasPlayedRef.current = true;
        handleChunkText(incoming.text);
        playAudio(audioData);
      }
    },
    [playAudio, scheduleWebAudioChunk, handleChunkText]
  );

  const streamingInitializedRef = useRef(false);
  const pendingCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (useStreaming && Platform.OS === "web") {
      pendingCheckIntervalRef.current = setInterval(() => {
        if (
          !isProcessingRef.current &&
          pendingBySeqRef.current.has(nextSeqRef.current) &&
          processPendingChunksRef.current
        ) {
          processPendingChunksRef.current().catch((error) => {
            console.error("Error in periodic processing", error);
          });
        }
      }, 100);

      return () => {
        if (pendingCheckIntervalRef.current) {
          clearInterval(pendingCheckIntervalRef.current);
          pendingCheckIntervalRef.current = null;
        }
      };
    }
  }, [useStreaming]);

  const resetSequence = useCallback(() => {
    nextSeqRef.current = 1;
    pendingBySeqRef.current.clear();
    if (Platform.OS === "web" && audioContextRef.current) {
      nextStartAtRef.current = audioContextRef.current.currentTime;
    }
  }, []);

  useEffect(() => {
    if (onResetSequence) {
      onResetSequence(resetSequence);
    }
  }, [onResetSequence, resetSequence]);

  useEffect(() => {
    if (useStreaming && onAudioQueueReady) {
      if (!streamingInitializedRef.current) {
        playedAudioChunksRef.current.clear();
        lastProcessedAudioRef.current = null;
        hasPlayedRef.current = false;
        hasStartedRef.current = false;
        nextSeqRef.current = 1;
        pendingBySeqRef.current.clear();
        if (Platform.OS === "web") {
          if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
          }
          nextStartAtRef.current = audioContextRef.current.currentTime;
        }
        streamingInitializedRef.current = true;
      }

      onAudioQueueReady((chunk) => {
        processIncomingChunk(chunk);
      });

      if (onStreamDone) {
        onStreamDone(() => {
          if (processRemainingChunksRef.current) {
            processRemainingChunksRef.current();
          }
        });
      }
    } else {
      streamingInitializedRef.current = false;
    }
  }, [useStreaming, onAudioQueueReady, processIncomingChunk, onStreamDone]);

  useEffect(() => {
    if (!useStreaming && initialIntroAudio && initialIntroAudio !== lastProcessedAudioRef.current) {
      const audioHash = initialIntroAudio;

      if (!playedAudioChunksRef.current.has(audioHash)) {
        playedAudioChunksRef.current.add(audioHash);
        lastProcessedAudioRef.current = initialIntroAudio;
        if (!hasPlayedRef.current) {
          hasPlayedRef.current = true;
        }
        playAudio(initialIntroAudio);
      } else {
        lastProcessedAudioRef.current = initialIntroAudio;
      }
    }
  }, [initialIntroAudio, playAudio, useStreaming]);

  // Cleanup meter on unmount
  useEffect(() => {
    return () => {
      if (meterRafIdRef.current) cancelAnimationFrame(meterRafIdRef.current);
      meterRafIdRef.current = null;
      analyserRef.current = null;
      analyserDataRef.current = null;
      meterGainRef.current = null;
    };
  }, []);

  return (
    <View style={styles.container}>
      <View style={styles.transcriptContainer}>
        <ScrollView
          ref={scrollViewRef}
          style={styles.scrollView}
          onContentSizeChange={() => {
            scrollViewRef.current?.scrollToEnd({ animated: true });
          }}>
          {introText ? (
            <CustomText
              text={introText}
              size="xl"
              weight="normal"
              style={styles.transcriptText}
              useTranslationText={false}
            />
          ) : null}
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    minHeight: ASSESSMENT_INTRO_DISPLAY_HEIGHT,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingVertical: 10,
    backgroundColor: "transparent",
    pointerEvents: "box-none",
  },
  transcriptContainer: {
    flex: 1,
    padding: 0,
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    backgroundColor: "transparent",
    pointerEvents: "box-none",
  },
  transcriptText: {
    width: "100%",
    maxWidth: 675,
    textAlign: "center",
    color: "#667085",
    backgroundColor: "transparent",
  },
  scrollView: {
    height: 150,
    width: "100%",
    backgroundColor: "transparent",
  },
});

export default TurnBasedAssessmentAudioRenderer;
